import React, {ChangeEvent, useState} from "react";
import {useDispatch, useSelector} from "react-redux";


import defaultAvatarImage from '../../../assets/images/avatar.svg';
import {ReactComponent as Globe} from "../../../assets/icons/icon_globe.svg";
import {useTranslation} from "react-i18next";
import {Employee} from "../../../app/interfaces";
import {selectAdminUser} from "../../../app/adminUser/selectors";
import {ReactComponent as EyeIcon} from '../../../assets/icons/eye.svg';
import {ReactComponent as EditIcon} from '../../../assets/icons/pencil.svg';

import {
    EmployeeAvatar, EmployeeData,
    EmployeeInfoBody,
    EmployeeInfoContainer,
    EmployeeInfoHeader, EmployeeSalary,
    HeaderAddress,
    HeaderInfo, JobDescriptionContainer, ModalJobDescriptionContainer, ModalJobDescriptionContent
} from "./styles";
import {
    CountryFlagIcon,
    Dropdown,
    FlexRow,
    InputEditable, Modal,
    StatusPill,
    StatusShortDescription,
    Tooltip
} from "../../../components";
import {fullNameForUser, isValidString, shortDescriptionForStatus} from "../../../utils";
import {EMPLOYMENT_STATUS_ACTIVE, EMPLOYMENT_STATUS_EOR_QUOTE_REQUESTED} from "../../../utils/constants";
import {downloadDocument} from "../../../services/documents";
import {currencySymbolByCode} from "../../../utils/currencies";
import {editEorCompany, editEorPartner} from "../../../app/employee/actions";
import {SalaryUpdateModal} from "../SalaryUpdate/SalaryUpdateModal";
import {countryNameByCode} from "../../../utils/countryCodes";

const EmployeeInfo = ({employee}:{employee:Employee}) => {
    const admin = useSelector(selectAdminUser)
    const {t} = useTranslation()
    const dispatch = useDispatch()
    const [eorPartner, setEorPartner] = useState<string>(employee?.eor_partner || '');
    const [eorCompany, setEorCompany] = useState<string>(employee?.eor_company || '');
    const [modalJobDescription, setModalJobDescription] = useState<boolean>(false);
    const [modalEditGrossSalary, setModalEditGrossSalary] = useState<boolean>(false);

    const handleEorPartnerChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEorPartner(e.target.value)
    }

    const handleSaveEorPartner = () => {
        dispatch(editEorPartner({
            employeeId: employee._id,
            companyId: employee.company._id,
            eor_partner: eorPartner
        }) as any)
    }

    const handleSaveEorCompany = () => {
        dispatch(editEorCompany({
            employeeId: employee._id,
            companyId: employee.company._id,
            eor_company: eorCompany
        }) as any)
    }

    const handleEorCompanyChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEorCompany(e.target.value)
    }

    return <EmployeeInfoContainer>
        <Modal  isOpen={modalJobDescription} onClickOutside={() => setModalJobDescription(false)}>
            <ModalJobDescriptionContainer>
                <ModalJobDescriptionContent>
                    <h1>{t('job_description')}</h1>
                    {employee.description}
                </ModalJobDescriptionContent>
            </ModalJobDescriptionContainer>
        </Modal>

        <SalaryUpdateModal
            employee={employee}
            isOpen={modalEditGrossSalary}
            onClose={() => setModalEditGrossSalary(false)}
        />

        <EmployeeInfoHeader>
            <FlexRow>
                {/*@ts-ignore*/}
                <EmployeeAvatar src={isValidString(employee?.avatar) ? `${process.env.REACT_APP_API_URL}/${employee.avatar}` : defaultAvatarImage}/>
                <HeaderInfo>
                    <HeaderAddress>
                        <div>
                            <Globe/>
                        </div>
                        <p>{countryNameByCode(employee?.tax_residence || '')}</p>
                        <div>
                            {isValidString(employee?.tax_residence) && <CountryFlagIcon countryCode={employee.tax_residence}/>}
                        </div>
                    </HeaderAddress>
                    <StatusPill status={employee.status}>
                        {t(employee.status)}
                    </StatusPill>
                    <StatusShortDescription status={employee.status}>
                        {t(shortDescriptionForStatus(employee.status))}
                    </StatusShortDescription>
                </HeaderInfo>
            </FlexRow>
            <Dropdown items={[
                ...(employee.status !== EMPLOYMENT_STATUS_EOR_QUOTE_REQUESTED && employee.hasOwnProperty('contract') && isValidString(employee.contract) ? [{
                    label: t('download_contract'),
                    onClick: () => downloadDocument(employee.contract, employee.company._id)
                }] : []),
                ...(employee.id_document && isValidString(employee.id_document) ? [{
                    label: t('download_id'),
                    onClick: () => downloadDocument(employee.id_document as string, employee.company._id)
                }]: [])
            ]}/>
        </EmployeeInfoHeader>
        <EmployeeInfoBody>
            <EmployeeSalary>
                <p>{`${employee.salary.amount}${currencySymbolByCode(employee.salary.currency || 'EUR')}`}</p>
                {employee.status === EMPLOYMENT_STATUS_ACTIVE && <button onClick={() => setModalEditGrossSalary(true)}>
                    <div className="icon_wrapper">
                        <EditIcon/>
                    </div>
                </button>}
            </EmployeeSalary>
            <EmployeeData>
                <label>{t('contract_start_date')}</label>
                {new Date(employee.start_date).toLocaleDateString(admin.locale, {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                })}
            </EmployeeData>
            <EmployeeData>
                <label>{t('termination_date')}</label>
                {new Date(employee.end_date).toLocaleDateString(admin.locale, {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                })}
            </EmployeeData>
            <EmployeeData>
                <label>{t('full_name')}</label>
                {fullNameForUser(employee)}
            </EmployeeData>
            <EmployeeData>
                <label>{t('company')}</label>
                {employee.company.name}
            </EmployeeData>
            <EmployeeData>
                <label>{t('eor_partner')}</label>
                <InputEditable value={eorPartner} id="eor_partner" name="eor_partner" onChange={handleEorPartnerChange} onSave={handleSaveEorPartner}/>
            </EmployeeData>
            <EmployeeData>
                <label>{t('eor_company')}</label>
                <InputEditable value={eorCompany} id="eor_company" name="eor_company" onChange={handleEorCompanyChange} onSave={handleSaveEorCompany}/>
            </EmployeeData>

            <EmployeeData>
                <FlexRow align="center">
                    <label>{t('email')}</label>
                    <Tooltip text={t('never_share_email')}/>
                </FlexRow>
                {employee.email}
            </EmployeeData>
            <EmployeeData>
                <label>{t('proxy_email')}</label>
                {employee.proxy_email}
            </EmployeeData>
            <EmployeeData>
                <label>{t('phone')}</label>
                {employee.phone.country_code ?? ''}{employee.phone.number ?? ''}
            </EmployeeData>
            <EmployeeData>
                <label>{t('tax_id_label')}</label>
                {employee.tax_id}
            </EmployeeData>
            <EmployeeData>
                <label>{t('address')}</label>
                {employee.address}
            </EmployeeData>
            <EmployeeData>
                <label>{t('iban')}</label>
                {employee.bank_data.iban}
            </EmployeeData>
            <EmployeeData>
                <label>{t('bic_swift_routing_number')}</label>
                {employee.bank_data.bic}
            </EmployeeData>
            <EmployeeData>
                <label>{t('position')}</label>
                {employee.activity}
            </EmployeeData>
            {employee.description && <JobDescriptionContainer>
                <label>{t('job_description')}</label>
                <div>
                    <p>{employee.description}</p>
                    <button onClick={() => setModalJobDescription((prev) => !prev)}>
                        <EyeIcon/>
                    </button>
                </div>
            </JobDescriptionContainer>}
            {employee.work_hours_per_week && <EmployeeData>
                <label>{t('working_hours')}</label>
                {employee.work_hours_per_week}
            </EmployeeData>}
            <EmployeeData>
                <label>{t('work_location_mode')}</label>
                {t(employee.work_location_mode)}
            </EmployeeData>
            <EmployeeData>
                <label>{t('probation_period')}</label>
                {t(employee.probation_period_in_months?.toString())}
            </EmployeeData>
            <EmployeeData>
                <label>{t('notice_period')}</label>
                {t(employee.notice_period_in_months?.toString())}
            </EmployeeData>
            <EmployeeData>
                <label>{t('work_engagement_type')}</label>
                {t(employee.work_engagement_type)}
            </EmployeeData>
        </EmployeeInfoBody>
    </EmployeeInfoContainer>
}

export default EmployeeInfo
